import { useState, useCallback } from "react";
import { ArrowLeft, Mail, KeyRound, CheckCircle2 } from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { PasswordRequirements, isPasswordValid } from "./PasswordRequirements";
import {
  forgotPassword,
  confirmForgotPassword,
} from "../../requestors/loginRequestor";
import axios from "axios";

type ForgotStep = "email" | "code" | "success";

interface ForgotPasswordFlowProps {
  onBack: () => void;
  initialEmail?: string;
  isDark: boolean;
}

const INPUT_CLASS =
  "w-full border rounded-lg outline-none transition-shadow focus:ring-2 focus:ring-login-accent focus:border-login-accent login-input bg-login-input-bg border-login-input-border text-login-input-text";

const PRIMARY_BTN_CLASS =
  "w-full rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center login-btn bg-login-btn-bg text-login-btn-text";

export const ForgotPasswordFlow = ({
  onBack,
  initialEmail = "",
  isDark,
}: ForgotPasswordFlowProps) => {
  const [step, setStep] = useState<ForgotStep>("email");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSendCode = useCallback(async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      let recaptchaToken = "";
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("forgot_password");
      }

      await forgotPassword(trimmedEmail, recaptchaToken);
      setStep("code");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorData = err.response?.data;
        if (errorData?.error === "RECAPTCHA_FAILED") {
          setError("reCAPTCHA verification failed. Please try again.");
        } else {
          // Always show success-like message to avoid leaking email existence
          setStep("code");
        }
      } else {
        setStep("code");
      }
    } finally {
      setIsLoading(false);
    }
  }, [email, executeRecaptcha]);

  const handleConfirmReset = async () => {
    if (!code.trim()) {
      setError("Please enter the verification code");
      return;
    }
    if (!isPasswordValid(newPassword)) {
      setError("Password does not meet all requirements");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await confirmForgotPassword(
        email.trim().toLowerCase(),
        code.trim(),
        newPassword
      );
      setStep("success");
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        const errorData = err.response?.data;
        if (errorData?.error === "CODE_EXPIRED") {
          setError(
            "Verification code has expired. Please request a new one."
          );
        } else if (errorData?.error === "CODE_INVALID") {
          setError(
            "Invalid verification code. Please check and try again."
          );
        } else if (errorData?.error === "INVALID_PASSWORD") {
          setError(
            errorData?.message || "Password does not meet requirements."
          );
        } else {
          setError("Failed to reset password. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter") action();
  };

  const ErrorBanner = () =>
    error ? (
      <div className="p-3 border rounded-lg px-4 py-2 font-body-primary bg-login-error-bg border-login-error-border text-login-error-text">
        {error}
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-3">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 font-normal transition-colors self-start hover:opacity-80 font-body-primary text-login-subtext"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to sign in
      </button>

      <h2 className="font-semibold font-heading-primary text-login-heading">
        {step === "success" ? "Password Reset" : "Reset your password"}
      </h2>

      <ErrorBanner />

      {step === "email" && (
        <>
          <p className="font-normal font-body-primary text-login-subtext">
            Enter your email address and we'll send you a verification code
            to reset your password.
          </p>
          <div>
            <label className="block font-medium mb-1 font-body-primary text-login-label">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail
                className="absolute left-3 top-1/2 -translate-y-1/2 login-icon text-login-subtext"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoFocus
                className={`${INPUT_CLASS} login-input-icon-left`}
                onKeyDown={(e) => handleKeyDown(e, handleSendCode)}
              />
            </div>
          </div>

          <button
            onClick={handleSendCode}
            disabled={isLoading || !email.trim()}
            className={PRIMARY_BTN_CLASS}
          >
            {isLoading ? "Sending..." : "Send verification code"}
          </button>
        </>
      )}

      {step === "code" && (
        <>
          <p className="font-normal font-body-primary text-login-subtext">
            We've sent a verification code to{" "}
            <span className="font-medium text-login-label">
              {email}
            </span>
            . Enter the code below along with your new password.
          </p>

          <div>
            <label className="block font-medium mb-1 font-body-primary text-login-label">
              Verification code <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <KeyRound
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-login-subtext"
              />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter 6-digit code"
                autoFocus
                className={`${INPUT_CLASS} login-input-icon-left`}
              />
            </div>
          </div>

          <div>
            <label className="block font-medium mb-1 font-body-primary text-login-label">
              New password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className={INPUT_CLASS}
            />
          </div>

          <PasswordRequirements password={newPassword} isDark={isDark} />

          <div>
            <label className="block font-medium mb-1 font-body-primary text-login-label">
              Confirm new password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={INPUT_CLASS}
              onKeyDown={(e) => handleKeyDown(e, handleConfirmReset)}
            />
          </div>

          {newPassword &&
            confirmPassword &&
            newPassword !== confirmPassword && (
              <p className="text-red-500 font-body-primary">
                Passwords do not match
              </p>
            )}

          <button
            onClick={handleConfirmReset}
            disabled={
              isLoading ||
              !code.trim() ||
              !isPasswordValid(newPassword) ||
              newPassword !== confirmPassword
            }
            className={PRIMARY_BTN_CLASS}
          >
            {isLoading ? "Resetting..." : "Reset password"}
          </button>

          <button
            onClick={() => {
              setStep("email");
              setError("");
            }}
            className="font-medium text-center transition-colors hover:opacity-80 font-body-primary text-login-accent"
          >
            Didn't receive a code? Send again
          </button>
        </>
      )}

      {step === "success" && (
        <div className="text-center py-4">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <p className="mb-4 font-body-primary text-login-label">
            Your password has been reset successfully.
          </p>
          <button
            onClick={onBack}
            className={PRIMARY_BTN_CLASS}
          >
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
};
